import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
  IconButton, Checkbox, FormControlLabel, MenuItem, Divider, Alert, Grid, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export const MEASUREMENTS = [
  { type: "WEIGHT", label: "Weight" },
  { type: "BLOOD_PRESSURE", label: "Blood pressure" },
  { type: "HEART_RATE", label: "Heart rate" },
  { type: "TEMPERATURE", label: "Temperature" },
  { type: "OXYGEN_SATURATION", label: "Oxygen saturation" },
];

export const WEEKDAYS = [
  { value: 1, label: "Monday" }, { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" }, { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

const defaultMonitoring = () =>
  Object.fromEntries(MEASUREMENTS.map((m) => [m.type, { enabled: false, frequency: "DAILY", dayOfWeek: 1 }]));

/**
 * Create or edit a plan. Goals and monitoring are sent as full lists — the server
 * makes the plan match them — and existing goals keep their id so a goal already
 * marked MET stays MET when you edit the plan around it.
 */
export default function CarePlanEditorDialog({ open, plan, saving, error, onClose, onSave }) {
  const isEdit = !!plan;
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [notes, setNotes] = useState("");
  const [goals, setGoals] = useState([]);
  const [monitoring, setMonitoring] = useState(defaultMonitoring());
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLocalError(null);
    if (plan) {
      setTitle(plan.title || "");
      setStartDate(plan.startDate || "");
      setNextReviewDate(plan.nextReviewDate || "");
      setNotes(plan.notes || "");
      setGoals(
        plan.goals.map((g) => ({ id: g.id, description: g.description, targetDate: g.targetDate || "", status: g.status }))
      );
      const m = defaultMonitoring();
      plan.monitoring.forEach((item) => {
        m[item.measurementType] = { enabled: true, frequency: item.frequency, dayOfWeek: item.dayOfWeek || 1 };
      });
      setMonitoring(m);
    } else {
      setTitle("");
      setStartDate("");
      setNextReviewDate("");
      setNotes("");
      setGoals([{ id: null, description: "", targetDate: "", status: "OPEN" }]);
      setMonitoring(defaultMonitoring());
    }
  }, [open, plan]);

  const updateGoal = (index, patch) =>
    setGoals((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  const updateMonitoring = (type, patch) => setMonitoring((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));

  const handleSave = () => {
    if (!title.trim()) {
      setLocalError("Plan title is required.");
      return;
    }
    if (goals.some((g) => g.id && !g.description.trim())) {
      setLocalError("An existing goal has no description. Remove it with the trash icon instead of clearing it.");
      return;
    }
    setLocalError(null);
    onSave({
      title: title.trim(),
      startDate: startDate || null,
      nextReviewDate: nextReviewDate || null,
      notes: notes.trim() || null,
      goals: goals
        .filter((g) => g.description.trim())
        .map((g) => ({ id: g.id ?? null, description: g.description.trim(), targetDate: g.targetDate || null, status: g.status || "OPEN" })),
      monitoring: MEASUREMENTS.filter((m) => monitoring[m.type].enabled).map((m) => ({
        measurementType: m.type,
        frequency: monitoring[m.type].frequency,
        dayOfWeek: monitoring[m.type].frequency === "WEEKLY" ? Number(monitoring[m.type].dayOfWeek) : null,
      })),
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit care plan" : "New care plan"}</DialogTitle>
      <DialogContent dividers>
        {(localError || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError || error}
          </Alert>
        )}

        <TextField
          label="Plan title" required fullWidth value={title} sx={{ mb: 2 }}
          onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 200 }}
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Start date" type="date" fullWidth value={startDate} InputLabelProps={{ shrink: true }}
              helperText="Leave blank to start on the day it's activated"
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Next review date" type="date" fullWidth value={nextReviewDate} InputLabelProps={{ shrink: true }}
              onChange={(e) => setNextReviewDate(e.target.value)}
            />
          </Grid>
        </Grid>
        <TextField label="Plan notes (the patient can see these)" multiline minRows={2} fullWidth value={notes} onChange={(e) => setNotes(e.target.value)} />

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Goals</Typography>
          <Button
            size="small" startIcon={<AddIcon />}
            onClick={() => setGoals((prev) => [...prev, { id: null, description: "", targetDate: "", status: "OPEN" }])}
          >
            Add goal
          </Button>
        </Box>
        {goals.length === 0 && <Typography variant="body2" color="text.secondary">No goals yet.</Typography>}
        {goals.map((g, i) => (
          <Grid container spacing={1.5} key={g.id ?? `new-${i}`} sx={{ mb: 1.5, alignItems: "center" }}>
            <Grid size={{ xs: 12, md: g.id ? 5 : 7 }}>
              <TextField
                label="Goal" size="small" fullWidth value={g.description} inputProps={{ maxLength: 500 }}
                onChange={(e) => updateGoal(i, { description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Target date" type="date" size="small" fullWidth value={g.targetDate}
                InputLabelProps={{ shrink: true }} onChange={(e) => updateGoal(i, { targetDate: e.target.value })}
              />
            </Grid>
            {g.id && (
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField select label="Status" size="small" fullWidth value={g.status} onChange={(e) => updateGoal(i, { status: e.target.value })}>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="MET">Met</MenuItem>
                  <MenuItem value="DROPPED">Dropped</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: "auto" }}>
              <Tooltip title="Remove goal">
                <IconButton size="small" onClick={() => setGoals((prev) => prev.filter((_, idx) => idx !== i))}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        ))}

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={700}>Monitoring schedule</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Routine readings the patient is asked to log. Symptom and safety questions stay on every
          patient's daily check-in regardless of what is chosen here.
        </Typography>
        {MEASUREMENTS.map((m) => {
          const v = monitoring[m.type];
          return (
            <Box key={m.type} sx={{ display: "flex", alignItems: "center", gap: 2, minHeight: 48, flexWrap: "wrap" }}>
              <FormControlLabel
                sx={{ minWidth: 190 }}
                control={<Checkbox checked={v.enabled} onChange={(e) => updateMonitoring(m.type, { enabled: e.target.checked })} />}
                label={m.label}
              />
              {v.enabled && (
                <>
                  <TextField select size="small" label="How often" value={v.frequency} sx={{ minWidth: 130 }}
                    onChange={(e) => updateMonitoring(m.type, { frequency: e.target.value })}>
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                  </TextField>
                  {v.frequency === "WEEKLY" && (
                    <TextField select size="small" label="Day" value={v.dayOfWeek} sx={{ minWidth: 140 }}
                      onChange={(e) => updateMonitoring(m.type, { dayOfWeek: e.target.value })}>
                      {WEEKDAYS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                    </TextField>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create draft"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}